import React, { Suspense, lazy } from 'react';
import type { CustomField } from '@puckeditor/core';

import { booleanField } from './boolean-field';
import { videoPosterField } from './video-fields';
import type { MediaAssetValue } from '../media-asset';

const MediaAssetWidget = lazy(() => import('./media-asset-widget'));

type MediaAssetFieldProps = {
  field: { label?: string };
  name: string;
  value?: MediaAssetValue | null;
  onChange: (value: MediaAssetValue) => void;
  readOnly?: boolean;
};

function MediaAssetFieldRender(props: MediaAssetFieldProps): JSX.Element {
  return (
    <Suspense fallback={null}>
      <MediaAssetWidget {...props} />
    </Suspense>
  );
}

/** Image-or-video asset: the type is detected, never chosen. The asset browser stays a lazy chunk. */
export const mediaAssetField: CustomField<MediaAssetValue> = {
  type: 'custom',
  label: 'Image or video',
  render: MediaAssetFieldRender as CustomField<MediaAssetValue>['render'],
  ai: { exclude: true },
};

/**
 * Flat fields a block spreads into its `fields` to accept an image or a video. `mediaPoster` and
 * `mediaDecorative` are the Video block's own poster and decorative-loop controls, shown only for a
 * video (see `resolveMediaAssetFields`).
 */
export const MEDIA_ASSET_FIELDS = {
  mediaAsset: mediaAssetField,
  mediaPoster: videoPosterField,
  mediaDecorative: booleanField(
    'Decorative loop',
    'True only for a short silent ambient clip with no spoken or essential content. Requires an MP4/WebM video and a poster.',
    'Plays muted and looping without controls and is hidden from screen readers. Phones and reduced-motion visitors see the poster only.'
  ),
};
